import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  XCircle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BADGE_BASE as BADGE,
  getStatusConfig,
  getQualityConfig,
  getStatusBadgeClasses,
} from "./channelStatus";

const Pulse = ({ className }) => <Skeleton className={className} />;

/**
 * O cartão de um canal de WhatsApp.
 *
 * Nasceu dentro da tela de Integrações e saiu de lá quando o Lucas passou a
 * pedir a escolha do canal: template é aprovado por canal, então ele precisa
 * mostrar os mesmos números que a pessoa vê em Integrações. Uma segunda versão
 * do cartão divergiria da primeira em silêncio — a pessoa reconheceria o número
 * mas não o objeto, que é o suficiente para ela duvidar de qual é qual.
 *
 * Só apresentação: quem sabe o que acontece no clique é quem usa. A tela de
 * Integrações abre a gaveta do canal; o Lucas abre os templates daquele canal.
 */

const STATUS_ICON = {
  green: CheckCircle,
  yellow: AlertTriangle,
  red: AlertCircle,
  gray: XCircle,
};

/** Foto do perfil na Meta; sem ela, o ícone que quem usa o cartão fornecer. */
function Avatar({ loading, profilePic, name, fallback }) {
  if (loading) return <Pulse className="w-10 h-10 rounded-lg shrink-0" />;
  if (!profilePic) return fallback;
  return (
    <img
      src={profilePic}
      alt={name}
      className="w-10 h-10 rounded-lg object-cover border border-border-strong shrink-0"
    />
  );
}

/** O estado do número na Meta, ou o que a plataforma sabe sem ela. */
function StatusBadge({ status, loading }) {
  if (loading) return <Pulse className="h-5 w-20" />;

  const cfg = status ? getStatusConfig(status) : null;
  if (!cfg) {
    // Sem resposta da Meta o cartão não inventa um estado: diz que o canal está
    // ativo na plataforma, que é o que a plataforma sabe sozinha.
    return (
      <span className={`${BADGE} bg-green-500/15 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 dark:border-green-500/20`}>
        <CheckCircle className="w-2.5 h-2.5" /> Ativo
      </span>
    );
  }

  const Icon = STATUS_ICON[cfg.color] || CheckCircle;
  return (
    <span className={`${BADGE} ${getStatusBadgeClasses(cfg.color)}`}>
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </span>
  );
}

function Badges({ status, quality, loading, badges }) {
  const qualityCfg = quality ? getQualityConfig(quality) : null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      {/* Canal que não é WhatsApp não tem estado na Meta nem nota de qualidade:
          ele traz os próprios selos, na mesma linha e na mesma geometria. */}
      {badges ?? <StatusBadge status={status} loading={loading} />}
      {!badges && qualityCfg && !loading && (
        <span className={`${BADGE} ${getStatusBadgeClasses(qualityCfg.color)}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${qualityCfg.dotClass}`} />
          {qualityCfg.label}
        </span>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.name — nome verificado na Meta, ou o nome do canal
 * @param {string} [props.number] — número como a Meta o exibe
 * @param {string} [props.profilePic]
 * @param {string} [props.status] — CONNECTED, FLAGGED… cru, traduzido aqui
 * @param {string} [props.quality] — GREEN, YELLOW, RED
 * @param {boolean} [props.loading] — perfil ainda vindo da Meta
 * @param {React.ReactNode} [props.fallbackAvatar] — ícone quando não há foto
 * @param {string} [props.href] — vira link em nova aba; sem ele, é clicável
 * @param {Function} [props.onClick]
 * @param {React.ReactNode} [props.badges] — substitui os selos de estado/qualidade
 * @param {React.ReactNode} [props.actions] — botões no canto, revelados no hover
 * @param {string} [props.className]
 */
export default function ChannelCard({
  name,
  number,
  profilePic,
  status,
  quality,
  loading = false,
  badges,
  fallbackAvatar,
  href,
  onClick,
  actions,
  className = "",
}) {
  const interactive = href || onClick;
  const shell = `bg-surface border border-border rounded-xl p-5 relative overflow-hidden group transition-colors ${
    interactive ? "hover:border-border-strong cursor-pointer" : ""
  } ${className}`;

  const body = (
    <>
      {actions && (
        <div className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          {actions}
        </div>
      )}
      <div className="flex items-start gap-3">
        <Avatar loading={loading} profilePic={profilePic} name={name} fallback={fallbackAvatar} />
        <div className="flex-1 min-w-0">
          {loading ? (
            <>
              <Pulse className="h-4 w-32 mb-1.5" />
              <Pulse className="h-3 w-28" />
            </>
          ) : (
            <>
              <h3 className="font-medium text-foreground-strong text-sm truncate">
                {name}
              </h3>
              <p className="text-xs text-foreground-muted mt-0.5 font-mono truncate">
                {number}
              </p>
            </>
          )}
          <Badges
            status={status}
            quality={quality}
            loading={loading}
            badges={badges}
          />
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      // Nova aba de propósito: a pessoa está no meio de uma conversa de suporte
      // e não pode perdê-la ao ir resolver.
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${shell} block no-underline`}
      >
        {body}
      </a>
    );
  }

  if (!onClick) return <div className={shell}>{body}</div>;

  /**
   * O clique é um `<button>` de verdade, esticado sobre o cartão.
   *
   * O cartão original era uma `div` com `onClick`: quem navega por teclado não
   * conseguia abri-lo. Trocar a `div` por um `<button>` também não serve —
   * `actions` traz os botões de editar e excluir, e botão dentro de botão é
   * HTML inválido. A camada invisível dá foco e tecla ao cartão inteiro, e os
   * botões de ação ficam acima dela pelo `z-10` que já tinham.
   */
  return (
    <div className={shell}>
      <button
        type="button"
        onClick={onClick}
        aria-label={name}
        className="absolute inset-0 cursor-pointer"
      />
      {body}
    </div>
  );
}
